import React from 'react';
import { Layers, Grid, Truck, Gem, Check, AlertTriangle, Thermometer, Wrench, Footprints, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
export function MaterialOptionsSection() {
  const materials = [{
    icon: Layers,
    title: 'Concrete (Poured)',
    description: 'The most common and versatile choice for heated systems. Cables or tubing are embedded directly in the slab for maximum thermal contact.',
    bestFor: ['Driveways (any size)', 'Walkways', 'Patios', 'Steps'],
    watchOuts: ['Can crack if base prep or mix design is poor', 'Requires control joints', 'Surface finish affects traction'],
    maintenance: 'Seal every 3-5 years; repair cracks early; avoid excessive salt',
    heatedNote: 'Excellent—direct embed means fastest response time and most even heat distribution',
    ratings: {
      traction: {
        label: 'Depends on Finish',
        color: 'bg-yellow-100 text-yellow-800'
      },
      durability: {
        label: 'Excellent',
        color: 'bg-green-100 text-green-800'
      },
      repairability: {
        label: 'Moderate',
        color: 'bg-yellow-100 text-yellow-800'
      },
      heatTransfer: {
        label: 'Excellent',
        color: 'bg-green-100 text-green-800'
      }
    }
  }, {
    icon: Grid,
    title: 'Interlock Pavers',
    description: 'Individual units laid on a sand/gravel base. Heating cables or mats sit on the base layer beneath the pavers. Beautiful and repairable.',
    bestFor: ['Driveways', 'Walkways', 'Patios where aesthetics matter', 'Areas needing future access'],
    watchOuts: ['Can shift/settle if base is inadequate', 'Joints can grow weeds', 'Sand can wash out', 'Snow plowing requires care'],
    maintenance: 'Re-sand joints periodically; reset shifted pavers; weed control; edge restraint inspection',
    heatedNote: 'Good—heat must transfer through sand layer, so response is slightly slower than direct-embed concrete',
    ratings: {
      traction: {
        label: 'Good',
        color: 'bg-green-100 text-green-800'
      },
      durability: {
        label: 'Very Good',
        color: 'bg-green-100 text-green-800'
      },
      repairability: {
        label: 'Excellent',
        color: 'bg-green-100 text-green-800'
      },
      heatTransfer: {
        label: 'Good',
        color: 'bg-green-100 text-green-800'
      }
    }
  }, {
    icon: Truck,
    title: 'Asphalt',
    description: 'Electric cables can be installed under asphalt. Lower upfront cost but shorter lifespan and more maintenance than concrete.',
    bestFor: ['Large driveways (budget priority)', 'Rural properties', 'Temporary solutions'],
    watchOuts: ['Softens in extreme heat', 'Shorter lifespan (15-20 yrs)', 'Surface degrades faster', 'Limited finish options'],
    maintenance: 'Seal coat every 2-3 years; patch cracks promptly; avoid fuel/oil spills',
    heatedNote: 'Fair—asphalt temperature during installation must be carefully managed; thermal cycling accelerates wear',
    ratings: {
      traction: {
        label: 'Good',
        color: 'bg-green-100 text-green-800'
      },
      durability: {
        label: 'Fair',
        color: 'bg-yellow-100 text-yellow-800'
      },
      repairability: {
        label: 'Easy',
        color: 'bg-green-100 text-green-800'
      },
      heatTransfer: {
        label: 'Good',
        color: 'bg-green-100 text-green-800'
      }
    }
  }, {
    icon: Gem,
    title: 'Natural Stone',
    description: 'Flagstone, granite, or other natural stone laid on a prepared base. Premium appearance with excellent durability, but highest cost.',
    bestFor: ['High-end patios', 'Decorative walkways', 'Accent areas', 'Appearance priority'],
    watchOuts: ['Expensive', 'Heavy (requires robust base)', 'Thickness varies (uneven heating)', 'Some stones slippery when wet'],
    maintenance: 'Re-point joints; seal porous stones; clean moss/algae; inspect for frost heave',
    heatedNote: 'Variable—thicker stones slow heat response; must be installed on proper base with heating elements below',
    ratings: {
      traction: {
        label: 'Varies',
        color: 'bg-yellow-100 text-yellow-800'
      },
      durability: {
        label: 'Excellent',
        color: 'bg-green-100 text-green-800'
      },
      repairability: {
        label: 'Moderate',
        color: 'bg-yellow-100 text-yellow-800'
      },
      heatTransfer: {
        label: 'Variable',
        color: 'bg-yellow-100 text-yellow-800'
      }
    }
  }];
  return <section id="material-options" className="py-16 bg-white md:py-24" data-id="element-687">
      <div className="container mx-auto px-4 md:px-6" data-id="element-688">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-689">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-690">
            Compare Your Material Options
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-691">
            Every material has pros and cons. We help you balance performance,
            longevity, and budget.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-692">
          {materials.map((material, index) => <Card key={index} className="border-2 border-slate-100 hover:border-primary/20 transition-all duration-300" data-id="element-693">
              <CardHeader className="pb-4" data-id="element-694">
                <div className="flex items-center gap-3 mb-2" data-id="element-695">
                  <div className="p-3 rounded-full bg-primary/10 text-primary" data-id="element-696">
                    <material.icon className="h-6 w-6" data-id="element-697" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-698">{material.title}</CardTitle>
                </div>
                <p className="text-slate-500 mt-2" data-id="element-699">{material.description}</p>

                <div className="flex flex-wrap gap-2 mt-4" data-id="element-700">
                  <span className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${material.ratings.durability.color}`} data-id="element-701">
                    <Shield className="h-3 w-3" data-id="element-702" /> Durability:{' '}
                    {material.ratings.durability.label}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${material.ratings.heatTransfer.color}`} data-id="element-703">
                    <Thermometer className="h-3 w-3" data-id="element-704" /> Heat Transfer:{' '}
                    {material.ratings.heatTransfer.label}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${material.ratings.repairability.color}`} data-id="element-705">
                    <Wrench className="h-3 w-3" data-id="element-706" /> Repair:{' '}
                    {material.ratings.repairability.label}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${material.ratings.traction.color}`} data-id="element-707">
                    <Footprints className="h-3 w-3" data-id="element-708" /> Traction:{' '}
                    {material.ratings.traction.label}
                  </span>
                </div>
              </CardHeader>

              <CardContent className="space-y-6" data-id="element-709">
                <div className="grid sm:grid-cols-2 gap-6" data-id="element-710">
                  <div data-id="element-711">
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm" data-id="element-712">
                      <Check className="h-4 w-4 text-green-500" data-id="element-713" /> Best For:
                    </h4>
                    <ul className="space-y-1" data-id="element-714">
                      {material.bestFor.map((item, i) => <li key={i} className="text-sm text-slate-600 flex items-start gap-2" data-id="element-715">
                          <span className="h-1 w-1 rounded-full bg-slate-400 mt-2 shrink-0" data-id="element-716"></span>
                          {item}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-717">
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm" data-id="element-718">
                      <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-719" />{' '}
                      Watch-Outs:
                    </h4>
                    <ul className="space-y-1" data-id="element-720">
                      {material.watchOuts.map((item, i) => <li key={i} className="text-sm text-slate-600 flex items-start gap-2" data-id="element-721">
                          <span className="h-1 w-1 rounded-full bg-slate-400 mt-2 shrink-0" data-id="element-722"></span>
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-723">
                  <h4 className="font-semibold text-slate-900 mb-1 flex items-center gap-2 text-sm" data-id="element-724">
                    <Thermometer className="h-4 w-4 text-primary" data-id="element-725" /> Heated
                    System Compatibility:
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-726">
                    {material.heatedNote}
                  </p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-727">
                  <h4 className="font-semibold text-slate-900 mb-1 flex items-center gap-2 text-sm" data-id="element-728">
                    <Wrench className="h-4 w-4 text-slate-500" data-id="element-729" /> Maintenance:
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-730">
                    {material.maintenance}
                  </p>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}